import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { mergeClassNames } from "../../utils/classes";
import Field from "./Field";
import LabelFor from "./LabelFor";

import type { FC, FormEvent, InputHTMLAttributes, KeyboardEvent, ReactNode, RefObject } from "react";

import * as styles from "./form.module.scss";

type InputElementAttributes = InputHTMLAttributes<HTMLInputElement>;

export type InputFieldProps = {
	formatter?: (value: string) => string;
	prefix?: string;
	postfix?: string;
	placeholder?: string;
	tooltip?: ReactNode;

	errors?: { errors: readonly unknown[] };
	inlineControls?: (props: { disabled: boolean | undefined; value: string | undefined }) => ReactNode;

	label?: ReactNode;
	hint?: string;

	onChange?: (val: string) => void;
	onFocus?: (e: FormEvent<HTMLInputElement>) => void;
	onBlur?: (e: FormEvent<HTMLInputElement>) => void;
	onClick?: (e: FormEvent<HTMLInputElement>) => void;

	testId?: string;
	labelTestId?: string;

	className?: string;
	labelClassName?: string;
	wrapperClassName?: string;

	innerRef?: RefObject<HTMLInputElement | null>;
	value: string | undefined;
	disabled?: boolean;
	/** Override the disabled state. When true, disables the input regardless of the `disabled` prop. */
	disabledOverride?: boolean;
	markAsRequired?: boolean;
	readOnly?: boolean;
	maxLength?: number;
	autoFocus?: boolean;
	nameForAutofill?: string;
	inputMode?: "none" | "text" | "tel" | "url" | "email" | "numeric" | "decimal" | "search";
	autoComplete?:
		| "off"
		| "name"
		| "given-name"
		| "family-name"
		| "email"
		| "username"
		| "new-password"
		| "current-password"
		| "one-time-code"
		| "organization"
		| "address-line1"
		| "address-line2"
		| "country-name"
		| "postal-code"
		| "tel"
		| "url";
	fieldProps?: InputElementAttributes;
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;

	/** Render extra content next to the label text (e.g., a label tooltip). */
	renderLabelExtra?: () => ReactNode;
	/** Full override of the bottom section (badge + validation). */
	renderFooter?: (errors: readonly unknown[] | undefined, testId: string | undefined) => ReactNode;
	/** Override just validation rendering. */
	renderValidation?: (errors: readonly unknown[] | undefined, testId: string | undefined) => ReactNode;
};

export const InputField: FC<InputFieldProps> = props => {
	const {
		inlineControls,
		value,
		disabled,
		disabledOverride,
		className,
		label,
		hint,
		errors,
		labelClassName,
		placeholder,
		tooltip,
		postfix,
		testId,
		fieldProps,
		nameForAutofill,
		labelTestId,
		inputMode,
		readOnly,
		maxLength,
		prefix,
		innerRef,
		markAsRequired = false,
		formatter,
		onChange,
		onBlur,
		onFocus,
		onClick,
		autoFocus,
		autoComplete,
		onKeyDown,
		wrapperClassName,
		renderLabelExtra,
		renderFooter,
		renderValidation,
	} = props;

	const resolvedDisabled = disabledOverride || disabled;

	if (postfix && inlineControls) {
		throw new Error(
			"InputField: 'postfix' and 'inlineControls' props are mutually exclusive and cannot be used together",
		);
	}

	const setValue = useCallback(
		(newValue: string) => {
			if (onChange) {
				onChange(newValue);
			}
		},
		[onChange],
	);

	const handleBlur = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			if (formatter) {
				const currentValue = e.currentTarget.value;
				const formatted = formatter(currentValue);

				if (currentValue !== formatted) {
					e.currentTarget.value = formatted;
					setValue(formatted);
				}
			}
			if (onBlur) {
				onBlur(e);
			}
		},
		[formatter, onBlur, setValue],
	);

	const handleFocus = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			if (onFocus) {
				onFocus(e);
			}
		},
		[onFocus],
	);

	const handleClick = useCallback(
		(e: FormEvent<HTMLInputElement>) => {
			if (onClick) {
				onClick(e);
			}
		},
		[onClick],
	);

	const inputType = fieldProps?.type ?? "text";
	const inputValue = value ?? "";

	const prefixRef = useRef<HTMLSpanElement>(null);
	const postfixRef = useRef<HTMLSpanElement>(null);
	const inlineControlsRef = useRef<HTMLSpanElement>(null);
	const [prefixPadding, setPrefixPadding] = useState<number | undefined>(undefined);
	const [postfixPadding, setPostfixPadding] = useState<number | undefined>(undefined);
	const [inlineControlsPadding, setInlineControlsPadding] = useState<number | undefined>(undefined);

	useLayoutEffect(
		function setInlinePaddings() {
			if (prefixRef.current) {
				setPrefixPadding(prefixRef.current.offsetWidth + 12);
			} else {
				setPrefixPadding(undefined);
			}
			if (postfixRef.current) {
				setPostfixPadding(postfixRef.current.offsetWidth + 12);
			} else {
				setPostfixPadding(undefined);
			}

			if (inlineControlsRef.current) {
				setInlineControlsPadding(inlineControlsRef.current.offsetWidth + 12);
			} else {
				setInlineControlsPadding(undefined);
			}
		},
		[prefix, postfix, resolvedDisabled, value, inlineControls],
	);

	const inputElement = (
		<input
			{...fieldProps}
			value={inputValue}
			type={inputType}
			onChange={e => onChange?.(e.currentTarget.value)}
			onBlur={e => handleBlur(e)}
			onFocus={e => handleFocus(e)}
			onClick={e => handleClick(e)}
			className={mergeClassNames(styles.field__input, className)}
			style={{
				paddingLeft: prefixPadding,
				paddingRight: Math.max(postfixPadding ?? 0, inlineControlsPadding ?? 0) || undefined,
			}}
			ref={innerRef}
			autoFocus={autoFocus}
			placeholder={placeholder}
			autoComplete={autoComplete ?? (nameForAutofill === undefined ? "off" : undefined)}
			name={nameForAutofill}
			disabled={resolvedDisabled}
			data-testid={testId}
			inputMode={inputMode}
			readOnly={readOnly}
			maxLength={maxLength}
			onKeyDown={onKeyDown}
			aria-required={markAsRequired}
		/>
	);

	const inputContent: ReactNode = inlineControls ? (
		<div style={{ position: "relative" }}>
			{inputElement}
			{
				<span
					ref={inlineControlsRef}
					style={{
						position: "absolute",
						right: 0,
						paddingRight: 12,
						display: "flex",
						alignItems: "center",
						top: 0,
						bottom: 0,
					}}
				>
					{inlineControls?.({ disabled, value })}
				</span>
			}
		</div>
	) : (
		inputElement
	);

	const renderInputWithPrefixPostfix = () => {
		if (!prefix && !postfix) {
			return inputContent;
		}
		return (
			<div className={styles.inputWrapper}>
				{prefix && (
					<span ref={prefixRef} className={styles.prefix}>
						{prefix}
					</span>
				)}
				{inputContent}
				{postfix && (
					<span ref={postfixRef} className={styles.postfix}>
						{postfix}
					</span>
				)}
			</div>
		);
	};

	return (
		<Field className={wrapperClassName}>
			<LabelFor
				label={label}
				errors={errors && errors.errors}
				className={labelClassName}
				testId={labelTestId}
				tooltip={tooltip}
				markAsRequired={markAsRequired}
				renderLabelExtra={renderLabelExtra}
				renderFooter={renderFooter}
				renderValidation={renderValidation}
			>
				{renderInputWithPrefixPostfix()}
				{hint && <small className={styles.hint}>{hint}</small>}
			</LabelFor>
		</Field>
	);
};
